import React from "react";
import { marinaCountrySlug, marinaDetailPath } from "@/lib/marinaUrls";

const SITE_URL = "https://marstick.com";

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function compactObject(value) {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== ""));
}

export default function MarinaStructuredData({ marina, reviews = [], lang = "en" }) {
  if (!marina) return null;

  const marinaUrl = `${SITE_URL}${marinaDetailPath(marina, lang)}`;
  const countryUrl = `${SITE_URL}/${lang}/marinas/${marinaCountrySlug(marina)}`;
  const realReviews = reviews.filter((review) => Number(review.rating) > 0);
  const reviewCount = realReviews.length;
  const averageRating = reviewCount
    ? realReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviewCount
    : null;

  const localBusiness = compactObject({
    "@type": "LocalBusiness",
    "@id": `${marinaUrl}#localbusiness`,
    name: marina.name,
    url: marinaUrl,
    image: marina.image_url,
    telephone: marina.phone,
    description: marina.description || `${marina.name} marina information, facilities, location and sailing planning details.`,
    address: compactObject({
      "@type": "PostalAddress",
      addressLocality: marina.city || marina.location,
      addressRegion: marina.region,
      addressCountry: marina.country,
    }),
    geo: Number.isFinite(Number(marina.latitude)) && Number.isFinite(Number(marina.longitude)) ? {
      "@type": "GeoCoordinates",
      latitude: Number(marina.latitude),
      longitude: Number(marina.longitude),
    } : undefined,
    amenityFeature: (marina.facilities || []).map((facility) => ({
      "@type": "LocationFeatureSpecification",
      name: facility,
      value: true,
    })),
    aggregateRating: reviewCount ? {
      "@type": "AggregateRating",
      ratingValue: Number(averageRating.toFixed(2)),
      reviewCount,
    } : undefined,
  });

  const breadcrumbs = {
    "@type": "BreadcrumbList",
    "@id": `${marinaUrl}#breadcrumbs`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/${lang}/` },
      { "@type": "ListItem", position: 2, name: "Marinas", item: `${SITE_URL}/${lang}/marinas` },
      { "@type": "ListItem", position: 3, name: marina.country || "Marinas", item: countryUrl },
      { "@type": "ListItem", position: 4, name: marina.name, item: marinaUrl },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({ "@context": "https://schema.org", "@graph": [localBusiness, breadcrumbs] }),
      }}
    />
  );
}